import React from 'react';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { H2, H3 } from '../Heading/Heading';
import { PrimaryButton, SecondaryButton } from '../Button/Button';
import IconAlertTriangle from '../IconAlertTriangle/IconAlertTriangle';
import IconErrorCircle from '../IconErrorCircle/IconErrorCircle';
import IconCircleSuccess from '../IconCircleSuccess/IconCircleSuccess';
import Modal from '../Modal/Modal';

import css from './ModalAlert.module.css';
import IconInfo from '../IconInfo/IconInfo';

const icons = {
  warning: IconAlertTriangle,
  error: IconErrorCircle,
  success: IconCircleSuccess,
  info: IconInfo,
};

const ModalAlert = ({
  isOpen,
  inProgress,
  heading,
  message,
  children,
  level,
  cancel,
  confirm,
  icon,
  onClose,
  onConfirm,
  messageClassName,
  contentClassName,
  footerClassName,
  ...modalProps
}) => {
  const Icon = icon === true ? icons[level] : icon;

  return (
    <Modal
      containerClassName={css.modalAlertContainer}
      contentClassName={css.modalAlertContent}
      closeButtonClassName={css.modalAlertCloseButton}
      closeButtonDisabled={inProgress}
      usePortal
      withoutLabel
      isOpen={isOpen}
      onClose={onClose}
      {...modalProps}
    >
      <H2 className={css.heading}>
        {heading || <FormattedMessage id={`ModalAlert.heading-${level}`} />}
      </H2>
      {Icon && <Icon className={css.icon} />}
      {message && (
        <H3 className={classNames(css.message, messageClassName)}>
          {message === true ? <FormattedMessage id="ModalAlert.message" /> : message}
        </H3>
      )}
      {children && <div className={classNames(css.content, contentClassName)}>{children}</div>}
      {(cancel || confirm) && (
        <div className={classNames(css.footer, footerClassName)}>
          {cancel && (
            <SecondaryButton onClick={onClose} disabled={inProgress}>
              {cancel === true ? <FormattedMessage id="ModalAlert.cancel" /> : cancel}
            </SecondaryButton>
          )}
          {confirm && (
            <PrimaryButton onClick={onConfirm} inProgress={inProgress}>
              {confirm === true ? <FormattedMessage id="ModalAlert.confirm" /> : confirm}
            </PrimaryButton>
          )}
        </div>
      )}
    </Modal>
  );
};

ModalAlert.defaultProps = {
  level: 'default',
};

const { bool, func, node, oneOf, oneOfType } = PropTypes;

ModalAlert.propTypes = {
  isOpen: bool,
  inProgress: bool,
  heading: node,
  message: oneOfType([bool, node]),
  children: node,
  level: oneOf(['default', 'warning', 'error', 'success', 'info']),
  cancel: oneOfType([bool, node]),
  confirm: oneOfType([bool, node]),
  icon: oneOfType([bool, node]),
  onClose: func.isRequired,
  onConfirm: func,
  onManageDisableScrolling: func.isRequired,
};

export default ModalAlert;
